// src/components/ErrorBoundary.jsx
// Without this, a single uncaught render error unmounts the whole React tree
// to a blank white screen — no message, no way to recover. This catches it,
// keeps the rest of the app usable, and shows the actual error so a crash on
// one page (e.g. Messages) is diagnosable instead of "the page is white".
import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Surface it in the console too (shows up in remote debugging / logcat).
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  // Recover when the user navigates away (route change) without a full reload.
  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.routeKey !== this.props.routeKey) {
      this.setState({ error: null, info: null });
    }
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{ padding: '24px 18px', maxWidth: 680, margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, color: '#b91c1c', marginBottom: 8 }}>
          Something on this page crashed
        </h2>
        <p style={{ fontSize: 14, color: '#374151', marginBottom: 14 }}>
          The rest of the app still works — use the menu to move to another page.
          If this keeps happening, screenshot the details below and send them.
        </p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <button
            onClick={() => this.setState({ error: null, info: null })}
            style={{ padding: '9px 16px', borderRadius: 10, border: 'none', background: '#ea6a1e', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '9px 16px', borderRadius: 10, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 700, cursor: 'pointer' }}
          >
            Reload app
          </button>
        </div>
        <pre style={{
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, lineHeight: 1.5,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#7f1d1d',
          borderRadius: 10, padding: 12, maxHeight: 320, overflow: 'auto',
        }}>
          {String(error?.stack || error?.message || error)}
          {info?.componentStack ? `\n\nComponent stack:${info.componentStack}` : ''}
        </pre>
      </div>
    );
  }
}
